import React, { memo, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import {
  ButtonDisclosure,
  ChecklistDropdown,
  Tooltip,
} from "@toddle-edu/ds-web";
import { AddCircleOutlined } from "@toddle-edu/ds-icons";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import { useDispatch } from "Hooks/useDispatch";
import { syncPlatformGradeScaleForTranscripts } from "../../../../module/AcademicRecordsThunks";
import { useManageColumnsDropdownData } from "./hooks";
import classes from "./ManageColumnsDropdown.scss";

const TEST_ID_PREFIX = "transcripts-studentDetails-manageColumns";
const getPopupContainer = () => document.body;

const styles = {
  tooltipOverlay: {
    maxWidth: "292px",
  },
};

const ManageColumnsDropdown = ({
  selectedGradeId,
  academicRecordsData,
  curriculumProgramsData,
  transcriptGradeScalesData,
  selectedKeys,
  onSelectionChange,
  idPrefix,
  onSyncSuccess,
  onSyncStart,
  onSyncEnd,
  isLoading,
}) => {
  const dispatch = useDispatch();
  const {
    resolvedSelectedKeys,
    dropdownOptions,
    handleSelectionChange,
    keyToOptionMap,
    t,
  } = useManageColumnsDropdownData({
    selectedGradeId,
    academicRecordsData,
    curriculumProgramsData,
    transcriptGradeScalesData,
    selectedKeys,
    onSelectionChange,
    idPrefix,
  });

  const handleSelectionChangeWithSync = useCallback(
    newSelectedKeys => {
      if (isLoading) return;

      const previousSelectedKeys = resolvedSelectedKeys;
      handleSelectionChange(newSelectedKeys);

      const newlyAddedKeys = _.difference(
        newSelectedKeys,
        previousSelectedKeys
      );
      const platformGradeScaleIds = _.compact(
        _.map(newlyAddedKeys, key => {
          const option = keyToOptionMap[key];
          const sourceId = _.get(option, "sourceId");

          if (!sourceId) {
            return null;
          }

          if (idPrefix && key.startsWith(idPrefix)) {
            return key.substring(idPrefix.length);
          }

          return key;
        })
      );

      if (_.isEmpty(platformGradeScaleIds)) {
        return;
      }

      onSyncStart();
      dispatch(
        syncPlatformGradeScaleForTranscripts({
          platformGradeScaleIds,
          onCompleted: ({ isSuccess, gradeScales }) => {
            onSyncEnd();
            if (isSuccess) {
              const syncedSourceToTranscriptIdMap = _.reduce(
                gradeScales || [],
                (acc, gradeScale) => {
                  const transcriptId = _.toString(_.get(gradeScale, "id", ""));
                  const sourceId = _.toString(
                    _.get(gradeScale, "sourceId", "")
                  );
                  if (!transcriptId || !sourceId) {
                    return acc;
                  }
                  acc[sourceId] = transcriptId;
                  return acc;
                },
                {}
              );

              if (!_.isEmpty(syncedSourceToTranscriptIdMap)) {
                const syncedKeyToOptionMap = _.mapValues(
                  keyToOptionMap,
                  option => {
                    const optionKey = _.toString(_.get(option, "key", ""));
                    const optionSourceId = _.get(option, "sourceId");

                    if (!optionSourceId || !optionKey) {
                      return option;
                    }

                    const rawId =
                      idPrefix && optionKey.startsWith(idPrefix)
                        ? optionKey.substring(idPrefix.length)
                        : optionKey;
                    const syncedTranscriptId = _.get(
                      syncedSourceToTranscriptIdMap,
                      rawId
                    );

                    if (!syncedTranscriptId) {
                      return option;
                    }

                    return {
                      ...option,
                      persistedKey: idPrefix
                        ? `${idPrefix}${syncedTranscriptId}`
                        : syncedTranscriptId,
                    };
                  }
                );

                onSelectionChange({
                  selectedKeys: newSelectedKeys,
                  keyToOptionMap: syncedKeyToOptionMap,
                });
              }

              onSyncSuccess();
              return;
            }

            handleSelectionChange(previousSelectedKeys);
          },
          onError: () => {
            onSyncEnd();
            handleSelectionChange(previousSelectedKeys);
          },
        })
      );
    },
    [
      dispatch,
      handleSelectionChange,
      idPrefix,
      isLoading,
      keyToOptionMap,
      onSelectionChange,
      onSyncEnd,
      onSyncStart,
      onSyncSuccess,
      resolvedSelectedKeys,
    ]
  );

  const skeletonOptions = useMemo(
    () =>
      _.times(7, index => ({
        key: `manage-columns-skeleton-${index}`,
        label: (
          <div className={classes.skeletonRow}>
            <SkeletonLoader width={177.961} height={20} />
          </div>
        ),
        isDisabled: true,
      })),
    []
  );

  const optionsToRender = isLoading ? skeletonOptions : dropdownOptions;
  const selectedKeysToRender = isLoading ? [] : resolvedSelectedKeys;
  const isDisabled = !isLoading && _.isEmpty(dropdownOptions);

  const manageColumnsButton = (
    <ButtonDisclosure
      size="medium"
      variant="progressive"
      type="inline"
      icon={<AddCircleOutlined />}
      testId={TEST_ID_PREFIX}
      disabled={isDisabled}
    >
      {t("transcript:manage_columns")}
    </ButtonDisclosure>
  );

  if (isDisabled) {
    return (
      <div className={classes.tooltipContainer}>
        <Tooltip
          placement="bottomLeft"
          showArrow={true}
          tooltip={t("transcript:manage_columns_disabled_tooltip")}
          overlayInnerStyle={styles.tooltipOverlay}
        >
          <div className={classes.tooltipButtonWrapper}>
            {manageColumnsButton}
          </div>
        </Tooltip>
      </div>
    );
  }

  return (
    <ChecklistDropdown
      useNewV2={true}
      testId={TEST_ID_PREFIX}
      options={optionsToRender}
      selectedKeys={selectedKeysToRender}
      onChange={handleSelectionChangeWithSync}
      checklistContainerClassName={classes.checklistContainer}
      isConfirmation={true}
      autoAdjustOverflow={true}
      primaryButtonLabel={t("common:add")}
      secondaryButtonLabel={t("common:cancel")}
      isSearchEnabled={false}
      isSelectAllEnabled={false}
      getPopupContainer={getPopupContainer}
    >
      {manageColumnsButton}
    </ChecklistDropdown>
  );
};

ManageColumnsDropdown.propTypes = {
  selectedGradeId: PropTypes.string,
  academicRecordsData: PropTypes.object,
  curriculumProgramsData: PropTypes.array,
  transcriptGradeScalesData: PropTypes.array,
  selectedKeys: PropTypes.arrayOf(PropTypes.string),
  onSelectionChange: PropTypes.func,
  idPrefix: PropTypes.string,
  onSyncSuccess: PropTypes.func,
  onSyncStart: PropTypes.func,
  onSyncEnd: PropTypes.func,
  isLoading: PropTypes.bool,
};

ManageColumnsDropdown.defaultProps = {
  selectedGradeId: null,
  academicRecordsData: null,
  curriculumProgramsData: [],
  transcriptGradeScalesData: [],
  selectedKeys: [],
  onSelectionChange: () => {},
  idPrefix: "",
  onSyncSuccess: () => {},
  onSyncStart: () => {},
  onSyncEnd: () => {},
  isLoading: false,
};

export default memo(ManageColumnsDropdown);
